import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Select } from 'components/ui/select'
import { Checkbox } from 'components/ui/checkbox'
import {
	initializeTemplates,
	initializeTemplateSiteDetails,
	createTemplate,
	updateTemplate,
	deleteTemplate,
	createTemplateSiteDetail,
	deleteTemplateSiteDetail,
} from 'slices/templateSlice'
import { GripVertical, Plus, Trash2, FileText, Loader2 } from 'lucide-react'
import notify from 'components/ui/notify'

// ─── Constants ──────────────────────────────────────────────────────────────────

const DETAIL_TYPE_OPTIONS = [
	{ value: 'Number', label: 'Number' },
	{ value: 'Currency', label: 'Currency' },
	{ value: 'Date', label: 'Date' },
]

// ─── Types ──────────────────────────────────────────────────────────────────────

interface DetailData {
	_id: string
	detailLabel: string
	detailType: string
	description: string
	isNew?: boolean
}

// ─── SortableRow ────────────────────────────────────────────────────────────────

interface SortableRowProps {
	id: string
	seq: number
	detail: DetailData
	onDetailLabelChange: (id: string, value: string) => void
	onDetailTypeChange: (id: string, value: string) => void
	onDescriptionChange: (id: string, value: string) => void
	onDelete: (id: string) => void
	isSelected: boolean
	onSelect: (id: string, checked: boolean) => void
}

const SortableRow = ({
	id,
	seq,
	detail,
	onDetailLabelChange,
	onDetailTypeChange,
	onDescriptionChange,
	onDelete,
	isSelected,
	onSelect,
}: SortableRowProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		zIndex: isDragging ? 10 : undefined,
		opacity: isDragging ? 0.8 : 1,
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`grid grid-cols-[24px_32px_1fr_120px_1fr_32px_32px] gap-1.5 py-1 px-1.5 items-center border-b border-gray-100 last:border-b-0 ${
				isDragging ? 'bg-blue-50 shadow-md rounded' : 'hover:bg-gray-50'
			}`}
		>
			<div
				{...attributes}
				{...listeners}
				className="flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
			>
				<GripVertical className="w-3.5 h-3.5" />
			</div>

			<div className="text-xs text-gray-500 text-center font-medium">{seq}</div>

			<Input
				value={detail.detailLabel}
				onChange={e => onDetailLabelChange(id, e.target.value)}
				className="h-7 text-xs"
				placeholder="Detail label"
			/>

			<Select value={detail.detailType} onChange={e => onDetailTypeChange(id, e.target.value)} className="h-7 text-xs">
				{DETAIL_TYPE_OPTIONS.map(opt => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</Select>

			<Input
				value={detail.description}
				onChange={e => onDescriptionChange(id, e.target.value)}
				className="h-7 text-xs"
				placeholder="Note"
			/>

			<div className="flex items-center justify-center">
				<Checkbox checked={isSelected} onCheckedChange={checked => onSelect(id, !!checked)} />
			</div>

			<button
				type="button"
				title="Delete detail"
				onClick={() => onDelete(id)}
				className="flex items-center justify-center bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-colors p-0"
			>
				<Trash2 className="w-3.5 h-3.5" />
			</button>
		</div>
	)
}

// ─── Main Component ─────────────────────────────────────────────────────────────

const SiteDetailsTemplateCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const { userInfo } = useSelector((state: any) => state.userLogin)

	// Redux template state
	const { entities: templateEntities, ids: templateIds, isInitialized } = useSelector((state: any) => state.templates)
	const {
		entities: detailEntities,
		ids: detailIds,
		isLoading: detailsLoading,
	} = useSelector((state: any) => state.templates.siteDetails)

	// Filter to siteDetail-type templates
	const siteDetailTemplates = useMemo(() => {
		const all = (templateIds || []).map((id: string) => templateEntities[id]).filter(Boolean)
		return all
			.filter((t: any) => t.templateType === 'sitedetail')
			.sort((a: any, b: any) => (a.templateName || '').localeCompare(b.templateName || ''))
	}, [templateIds, templateEntities])

	// ─── Editor State ───────────────────────────────────────────────────────────

	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
	const [isCreateMode, setIsCreateMode] = useState(false)
	const [templateName, setTemplateName] = useState('')
	const [description, setDescription] = useState('')
	const [accessLevel, setAccessLevel] = useState('private')
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [detailOrder, setDetailOrder] = useState<string[]>([])
	const [detailsMap, setDetailsMap] = useState<Record<string, DetailData>>({})
	const [selectedDetails, setSelectedDetails] = useState<Set<string>>(new Set())
	const [isDirty, setIsDirty] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [originalDetailIds, setOriginalDetailIds] = useState<Set<string>>(new Set())
	const nextTempId = useRef(0)

	// ─── Init ───────────────────────────────────────────────────────────────────

	useEffect(() => {
		;(dispatch as any)(initializeTemplates())
	}, [dispatch])

	// ─── Load template details when selection changes ───────────────────────────

	useEffect(() => {
		if (selectedTemplateId) {
			;(dispatch as any)(initializeTemplateSiteDetails(selectedTemplateId))
		}
	}, [dispatch, selectedTemplateId])

	// Populate editor when template details arrive
	useEffect(() => {
		if (!selectedTemplateId || isCreateMode) return
		const template = templateEntities[selectedTemplateId]
		if (!template) return

		setTemplateName(template.templateName || '')
		setDescription(template.description || '')
		setAccessLevel(template.accessLevel || 'private')

		const loaded = (detailIds || [])
			.map((id: string) => detailEntities[id])
			.filter(Boolean)
			.sort((a: any, b: any) => (a.seq || 0) - (b.seq || 0))

		const order: string[] = []
		const map: Record<string, DetailData> = {}
		const origIds = new Set<string>()
		loaded.forEach((d: any) => {
			order.push(d._id)
			map[d._id] = {
				_id: d._id,
				detailLabel: d.detailLabel || '',
				detailType: d.detailType || 'Number',
				description: d.description || '',
			}
			origIds.add(d._id)
		})
		setDetailOrder(order)
		setDetailsMap(map)
		setOriginalDetailIds(origIds)
		setSelectedDetails(new Set())
		setErrors({})
		setIsDirty(false)
	}, [detailIds, detailEntities, selectedTemplateId, isCreateMode])

	// ─── DnD ────────────────────────────────────────────────────────────────────

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	)

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (over && active.id !== over.id) {
			setDetailOrder(prev => {
				const oldIndex = prev.indexOf(active.id as string)
				const newIndex = prev.indexOf(over.id as string)
				return arrayMove(prev, oldIndex, newIndex)
			})
			setIsDirty(true)
		}
	}

	// ─── Detail Handlers ────────────────────────────────────────────────────────

	const handleDetailLabelChange = (id: string, value: string) => {
		setDetailsMap(prev => ({ ...prev, [id]: { ...prev[id], detailLabel: value } }))
		setIsDirty(true)
	}

	const handleDetailTypeChange = (id: string, value: string) => {
		setDetailsMap(prev => ({ ...prev, [id]: { ...prev[id], detailType: value } }))
		setIsDirty(true)
	}

	const handleDescriptionChange = (id: string, value: string) => {
		setDetailsMap(prev => ({ ...prev, [id]: { ...prev[id], description: value } }))
		setIsDirty(true)
	}

	const handleDetailSelect = (id: string, checked: boolean) => {
		const newSet = new Set(selectedDetails)
		if (checked) newSet.add(id)
		else newSet.delete(id)
		setSelectedDetails(newSet)
	}

	const handleSelectAll = (checked: boolean) => {
		setSelectedDetails(checked ? new Set(detailOrder) : new Set())
	}

	const isAllSelected = detailOrder.length > 0 && detailOrder.every(id => selectedDetails.has(id))

	const handleDeleteDetail = (id: string) => {
		const detail = detailsMap[id]
		const name = detail?.detailLabel?.trim() || 'this detail'
		notify({
			title: 'Delete Detail',
			text: `Are you sure you want to delete "${name}"?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setDetailOrder(prev => prev.filter(did => did !== id))
				setDetailsMap(prev => {
					const next = { ...prev }
					delete next[id]
					return next
				})
				setSelectedDetails(prev => {
					const next = new Set(prev)
					next.delete(id)
					return next
				})
				setIsDirty(true)
			}
		})
	}

	const handleDeleteSelected = () => {
		if (selectedDetails.size === 0) return
		notify({
			title: 'Delete Selected Details',
			text: `Are you sure you want to delete ${selectedDetails.size} detail${selectedDetails.size > 1 ? 's' : ''}?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setDetailOrder(prev => prev.filter(id => !selectedDetails.has(id)))
				setDetailsMap(prev => {
					const next = { ...prev }
					selectedDetails.forEach(id => delete next[id])
					return next
				})
				setSelectedDetails(new Set())
				setIsDirty(true)
			}
		})
	}

	const handleAddDetail = () => {
		const tempId = `_new_${nextTempId.current++}`
		const newDetail: DetailData = {
			_id: tempId,
			detailLabel: '',
			detailType: 'Number',
			description: '',
			isNew: true,
		}
		setDetailOrder(prev => [...prev, tempId])
		setDetailsMap(prev => ({ ...prev, [tempId]: newDetail }))
		setIsDirty(true)
	}

	// ─── Template List Handlers ─────────────────────────────────────────────────

	const switchTemplate = useCallback((templateId: string | null, createMode: boolean) => {
		setSelectedTemplateId(templateId)
		setIsCreateMode(createMode)
		if (createMode) {
			setTemplateName('')
			setDescription('')
			setAccessLevel('private')
			setDetailOrder([])
			setDetailsMap({})
			setOriginalDetailIds(new Set())
			setSelectedDetails(new Set())
			setErrors({})
			setIsDirty(false)
		}
	}, [])

	const handleTemplateClick = useCallback(
		(templateId: string) => {
			if (templateId === selectedTemplateId) return
			const doSwitch = () => switchTemplate(templateId, false)
			if (isDirty) {
				;notify({
					text: 'You have unsaved changes. Discard them?',
					buttons: ['Cancel', 'Discard'],
					dangerMode: true,
				}).then((value: any) => {
					if (value) doSwitch()
				})
			} else {
				doSwitch()
			}
		},
		[selectedTemplateId, isDirty, switchTemplate]
	)

	const handleNewTemplate = useCallback(() => {
		const doSwitch = () => switchTemplate(null, true)
		if (isDirty) {
			;notify({
				text: 'You have unsaved changes. Discard them?',
				buttons: ['Cancel', 'Discard'],
				dangerMode: true,
			}).then((value: any) => {
				if (value) doSwitch()
			})
		} else {
			doSwitch()
		}
	}, [isDirty, switchTemplate])

	const handleDeleteTemplate = useCallback(
		(e: React.MouseEvent, templateId: string) => {
			e.stopPropagation()
			const template = templateEntities[templateId]
			;notify({
				text: `Delete template "${template?.templateName}"? This cannot be undone.`,
				buttons: ['Cancel', 'Delete'],
				dangerMode: true,
			}).then(async (value: any) => {
				if (value) {
					await (dispatch as any)(deleteTemplate(templateId))
					;(dispatch as any)(initializeTemplates())
					if (selectedTemplateId === templateId) {
						switchTemplate(null, false)
					}
				}
			})
		},
		[dispatch, templateEntities, selectedTemplateId, switchTemplate]
	)

	// ─── Save ───────────────────────────────────────────────────────────────────

	const validate = () => {
		const newErrors: Record<string, string> = {}
		if (!templateName.trim()) newErrors.templateName = 'Template name is required'
		if (!description.trim()) newErrors.description = 'Description is required'
		const emptyDetail = detailOrder.find(id => !detailsMap[id]?.detailLabel.trim())
		if (emptyDetail) newErrors.details = 'All details must have a label'
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSave = async () => {
		if (!validate()) return
		setIsSaving(true)

		try {
			if (isCreateMode) {
				// Create template
				const result = await (dispatch as any)(
					createTemplate({
						templateName: templateName.trim(),
						description: description.trim(),
						templateType: 'sitedetail',
						accessLevel,
					})
				)
				const newTemplateId = result.payload?._id
				if (!newTemplateId) throw new Error('Failed to create template')

				// Create details
				for (let i = 0; i < detailOrder.length; i++) {
					const d = detailsMap[detailOrder[i]]
					await (dispatch as any)(
						createTemplateSiteDetail({
							templateId: newTemplateId,
							detailLabel: d.detailLabel.trim(),
							seq: i + 1,
							detailType: d.detailType,
							description: d.description.trim(),
						})
					)
				}

				await (dispatch as any)(initializeTemplates())
				switchTemplate(newTemplateId, false)
				notify('Created!', `Template "${templateName.trim()}" has been created.`, 'success')
			} else if (selectedTemplateId) {
				// Update template metadata
				await (dispatch as any)(
					updateTemplate({
						_id: selectedTemplateId,
						templateName: templateName.trim(),
						description: description.trim(),
						accessLevel,
					})
				)

				// Delete all existing details then recreate in new order
				for (const origId of originalDetailIds) {
					await (dispatch as any)(deleteTemplateSiteDetail(origId))
				}
				for (let i = 0; i < detailOrder.length; i++) {
					const d = detailsMap[detailOrder[i]]
					await (dispatch as any)(
						createTemplateSiteDetail({
							templateId: selectedTemplateId,
							detailLabel: d.detailLabel.trim(),
							seq: i + 1,
							detailType: d.detailType,
							description: d.description.trim(),
						})
					)
				}

				await (dispatch as any)(initializeTemplates())
				await (dispatch as any)(initializeTemplateSiteDetails(selectedTemplateId))
				notify('Saved!', `Template "${templateName.trim()}" has been updated.`, 'success')
			}
			setIsDirty(false)
		} catch (err: any) {
			notify('Error', err.message || 'Failed to save template', 'error')
		} finally {
			setIsSaving(false)
		}
	}

	// ─── Render ─────────────────────────────────────────────────────────────────

	const isEditorActive = isCreateMode || selectedTemplateId !== null
	const isSaveDisabled = !templateName.trim() || !description.trim() || isSaving

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">Site Detail Templates</h2>

						<div className="max-w-[1080px] mx-auto w-full mt-4 flex flex-row gap-4 min-h-[600px]">
							{/* ── Left Panel: Template List ────────────────────────────── */}
							<div className="w-[260px] shrink-0 bg-white rounded-lg border border-gray-200 flex flex-col">
								<div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
									<span className="text-sm font-semibold text-gray-700">Templates</span>
									<span className="text-xs text-gray-400">{siteDetailTemplates.length}</span>
								</div>

								<div className="flex-1 overflow-y-auto">
									{!isInitialized ? (
										<div className="flex items-center justify-center py-10">
											<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
										</div>
									) : siteDetailTemplates.length === 0 ? (
										<div className="py-10 text-center text-sm text-gray-400">No templates yet</div>
									) : (
										siteDetailTemplates.map((template: any) => (
											<div
												key={template._id}
												onClick={() => handleTemplateClick(template._id)}
												className={`group flex items-center justify-between px-3 py-2 cursor-pointer border-l-2 transition-colors ${
													selectedTemplateId === template._id
														? 'bg-[#0080fe]/5 text-[#0080fe] border-[#0080fe] font-semibold'
														: 'border-transparent hover:bg-gray-50 text-gray-700'
												}`}
											>
												<div className="min-w-0 flex-1">
													<div className="text-sm truncate">{template.templateName}</div>
													{template.description && (
														<div className="text-[11px] text-gray-400 truncate mt-0.5">{template.description}</div>
													)}
												</div>
												<button
													type="button"
													title="Delete template"
													onClick={e => handleDeleteTemplate(e, template._id)}
													className="opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-all p-1 shrink-0"
												>
													<Trash2 className="w-3.5 h-3.5" />
												</button>
											</div>
										))
									)}
								</div>

								<div className="px-3 py-2 border-t border-gray-200">
									<Button variant="ghost" onClick={handleNewTemplate} className="w-full justify-start">
										<Plus className="w-4 h-4 mr-1.5" />
										New Template
									</Button>
								</div>
							</div>

							{/* ── Right Panel: Template Editor ─────────────────────────── */}
							<div className="flex-1 bg-white rounded-lg border border-gray-200 p-5 flex flex-col">
								{!isEditorActive ? (
									<div className="flex-1 flex flex-col items-center justify-center text-gray-400">
										<FileText className="w-12 h-12 mb-3 text-gray-300" />
										<p className="text-sm">Select a template or create a new one</p>
									</div>
								) : (
									<>
										{/* Header Fields */}
										<div className="flex flex-col gap-2 mb-3">
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Template Name <span className="text-red-500">*</span>
												</label>
												<Input
													value={templateName}
													onChange={e => {
														setTemplateName(e.target.value)
														setIsDirty(true)
														if (errors.templateName) setErrors({ ...errors, templateName: '' })
													}}
													className={`text-xs h-8 ${errors.templateName ? 'border-red-500' : ''}`}
													placeholder="Template Name"
												/>
												{errors.templateName && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">{errors.templateName}</p>
												)}
											</div>
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Description <span className="text-red-500">*</span>
												</label>
												<Textarea
													value={description}
													onChange={e => {
														setDescription(e.target.value)
														setIsDirty(true)
														if (errors.description) setErrors({ ...errors, description: '' })
													}}
													className={`text-xs min-h-[3.5rem] ${errors.description ? 'border-red-500' : ''}`}
													placeholder="Describe the purpose of this template"
													rows={2}
												/>
												{errors.description && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">{errors.description}</p>
												)}
											</div>
										</div>

										{/* Access Level */}
										<div className="flex flex-row gap-4 mb-3 p-2 bg-gray-50 rounded text-xs">
											<span className="font-semibold text-gray-700 mr-2">Access:</span>
											{['private', 'org-wide', 'public'].map(level => (
												<label key={level} className="flex items-center gap-1.5 cursor-pointer">
													<input
														type="radio"
														name="accessLevel"
														value={level}
														checked={accessLevel === level}
														onChange={e => {
															setAccessLevel(e.target.value)
															setIsDirty(true)
														}}
														className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
													/>
													{level === 'private' ? 'Private' : level === 'org-wide' ? 'Org-Wide' : 'Public'}
												</label>
											))}
										</div>

										{/* Detail Table */}
										<div className="flex-1 overflow-y-auto border border-gray-200 rounded mb-3">
											{/* Header */}
											<div className="grid grid-cols-[24px_32px_1fr_120px_1fr_32px_32px] gap-1.5 px-1.5 py-1.5 border-b border-gray-200 bg-gray-50 font-semibold text-xs sticky top-0 z-10">
												<span></span>
												<span className="text-center">Seq</span>
												<span>Detail Label</span>
												<span>Type</span>
												<span>Note</span>
												<label className="flex items-center justify-center cursor-pointer">
													<Checkbox checked={isAllSelected} onCheckedChange={checked => handleSelectAll(!!checked)} />
												</label>
												<span></span>
											</div>

											{detailsLoading && !isCreateMode ? (
												<div className="flex items-center justify-center py-8">
													<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
												</div>
											) : detailOrder.length === 0 ? (
												<div className="py-8 text-center text-sm text-gray-400">
													No details yet. Click "+ Add Detail" to get started.
												</div>
											) : (
												<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
													<SortableContext items={detailOrder} strategy={verticalListSortingStrategy}>
														{detailOrder.map((id, index) => {
															const detail = detailsMap[id]
															if (!detail) return null
															return (
																<SortableRow
																	key={id}
																	id={id}
																	seq={index + 1}
																	detail={detail}
																	onDetailLabelChange={handleDetailLabelChange}
																	onDetailTypeChange={handleDetailTypeChange}
																	onDescriptionChange={handleDescriptionChange}
																	onDelete={handleDeleteDetail}
																	isSelected={selectedDetails.has(id)}
																	onSelect={handleDetailSelect}
																/>
															)
														})}
													</SortableContext>
												</DndContext>
											)}
											{errors.details && <p className="text-red-500 text-[0.7rem] px-3 py-1">{errors.details}</p>}
										</div>

										{/* Actions */}
										<div className="flex items-center justify-between gap-2">
											<div className="flex items-center gap-2">
												<Button variant="ghost" onClick={handleAddDetail}>
													<Plus className="w-4 h-4 mr-1" />
													Add Detail
												</Button>
												{selectedDetails.size > 0 && (
													<Button variant="ghost-red" onClick={handleDeleteSelected}>
														<Trash2 className="w-4 h-4 mr-1" />
														Delete Selected ({selectedDetails.size})
													</Button>
												)}
											</div>
											<Button onClick={handleSave} disabled={isSaveDisabled}>
												{isSaving ? (
													<>
														<Loader2 className="w-4 h-4 mr-1 animate-spin" />
														Saving...
													</>
												) : (
													<>{isCreateMode ? 'Create Template' : 'Save Template'}</>
												)}
											</Button>
										</div>
									</>
								)}
							</div>
						</div>
					</main>
				</div>
			</div>
		</div>
	)
}

export default SiteDetailsTemplateCN
